import React, { useMemo, useState } from "react";
import { ArrowUp } from "lucide-react";

import { cn } from "@/lib/utils";

type ChatMessage = {
  id: number;
  role: "user" | "assistant";
  text: string;
};

interface ChatSkeletonProps {
  className?: string;
}

const INITIAL_MESSAGES: ChatMessage[] = [
  {
    id: 1,
    role: "assistant",
    text: "Welcome! This chat experience is in skeleton mode for now.",
  },
];

const ChatSkeleton = ({ className }: ChatSkeletonProps) => {
  const [messages, setMessages] = useState<ChatMessage[]>(INITIAL_MESSAGES);
  const [draft, setDraft] = useState("");
  const nextId = useMemo(() => messages.length + 1, [messages.length]);

  const handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const trimmed = draft.trim();
    if (!trimmed) return;
    setMessages((prev) => [
      ...prev,
      {
        id: nextId,
        role: "user",
        text: trimmed,
      },
    ]);
    setDraft("");
  };

  return (
    <div className={cn("flex min-h-0 flex-1 flex-col gap-4 px-4 pb-4", className)}>
      <div className="flex min-h-0 flex-1 overflow-y-auto rounded-2xl border border-slate-200 bg-white p-4 text-slate-900">
        <div className="flex flex-col gap-3">
          {messages.map((message) => (
            <div
              key={message.id}
              className={cn(
                "max-w-[80%] rounded-2xl px-3 py-2 text-sm",
                message.role === "user"
                  ? "ml-auto bg-chat-red text-white"
                  : "bg-slate-100 text-slate-900",
              )}
            >
              {message.text}
            </div>
          ))}
        </div>
      </div>
      <form className="relative" onSubmit={handleSubmit} data-no-drag="true">
        <input
          type="text"
          value={draft}
          onChange={(event) => setDraft(event.target.value)}
          placeholder="Draft a message..."
          className="h-11 w-full rounded-full border border-slate-200 bg-white px-4 pr-14 text-sm text-slate-900 placeholder:text-slate-400 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-chat-red/40"
        />
        <button
          type="submit"
          aria-label="Send message"
          className="absolute right-1.5 top-1/2 flex h-10 w-10 -translate-y-1/2 items-center justify-center rounded-full border-2 border-chat-red/80 bg-white text-chat-red shadow-sm transition hover:border-chat-red hover:text-chat-red/90"
        >
          <ArrowUp className="h-5 w-5" />
        </button>
      </form>
    </div>
  );
};

export default ChatSkeleton;
